/**
 * Site header — mega-menu dropdown toggles + mobile hamburger.
 *
 * Desktop: [data-nav-toggle] buttons open their sibling panel (one at
 * a time), close on Esc / outside click. Mobile: [data-nav-burger]
 * toggles the stacked panel; [data-mobile-toggle] accordions the
 * sub-lists.
 */
export default function initSiteHeader() {
	const header = document.querySelector('[data-site-header]');
	if (!header) return;

	const toggles = Array.from(header.querySelectorAll('[data-nav-toggle]'));

	const closeAll = (except = null) => {
		toggles.forEach((btn) => {
			if (btn === except) return;
			btn.setAttribute('aria-expanded', 'false');
			btn.closest('.main-nav__item')?.classList.remove('is-open');
		});
	};

	toggles.forEach((btn) => {
		btn.addEventListener('click', () => {
			const isOpen = btn.getAttribute('aria-expanded') === 'true';
			closeAll(btn);
			btn.setAttribute('aria-expanded', String(!isOpen));
			btn.closest('.main-nav__item')?.classList.toggle('is-open', !isOpen);
		});
	});

	document.addEventListener('click', (e) => {
		if (!header.contains(e.target)) closeAll();
	});

	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape') closeAll();
	});

	// Search dropdown: icon toggles the panel; focus the input on open;
	// close on Esc / outside click (shares the nav's document listeners).
	const searchWrap = header.querySelector('[data-search-wrap]');
	const searchToggle = header.querySelector('[data-search-toggle]');
	const searchPanel = header.querySelector('[data-search-panel]');
	const closeSearch = () => {
		if (!searchToggle || !searchPanel) return;
		searchToggle.setAttribute('aria-expanded', 'false');
		searchPanel.hidden = true;
	};
	if (searchToggle && searchPanel) {
		searchToggle.addEventListener('click', () => {
			const isOpen = searchToggle.getAttribute('aria-expanded') === 'true';
			closeAll();
			closeMobileNav();
			searchToggle.setAttribute('aria-expanded', String(!isOpen));
			searchPanel.hidden = isOpen;
			if (!isOpen) searchPanel.querySelector('[data-search-input]')?.focus();
		});
		document.addEventListener('click', (e) => {
			if (searchWrap && !searchWrap.contains(e.target)) closeSearch();
		});
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') closeSearch();
		});
	}

	// Mobile: the panel is position:fixed below the bar — measure where the
	// bar actually ends (admin bar shifts it) and lock body scroll while open.
	const burger = header.querySelector('[data-nav-burger]');
	const mobilePanel = header.querySelector('#site-nav-mobile');

	/**
	 * The panel is a full-viewport opaque overlay, but it's the last element in
	 * <header> — so tabbing past its final link used to land in <main>, on
	 * elements completely covered by it (WCAG 2.2 2.4.11 Focus Not Obscured).
	 *
	 * `inert` is the whole fix: it takes the rest of the page out of the tab
	 * order AND the accessibility tree, so focus simply cannot leave the panel
	 * and a screen reader can't read what's behind it either.
	 */
	const setBackgroundInert = (inert) => {
		if (!mobilePanel) return;
		Array.from(document.body.children).forEach((el) => {
			if (el.contains(mobilePanel)) return; // the header holds the panel
			el.inert = inert;
		});
	};

	const closeMobileNav = ({ returnFocus = false } = {}) => {
		if (!burger || !mobilePanel) return;
		const wasOpen = burger.getAttribute('aria-expanded') === 'true';
		burger.setAttribute('aria-expanded', 'false');
		mobilePanel.hidden = true;
		document.documentElement.classList.remove('mobile-nav-open');
		setBackgroundInert(false);
		// Send focus back to the control that opened it, rather than letting it
		// reset to <body> when the focused element is hidden underneath us.
		if (wasOpen && returnFocus) burger.focus();
	};

	if (burger && mobilePanel) {
		burger.addEventListener('click', () => {
			const isOpen = burger.getAttribute('aria-expanded') === 'true';
			if (isOpen) {
				closeMobileNav();
				return;
			}
			closeSearch();
			document.documentElement.style.setProperty('--gg-header-bottom', `${header.getBoundingClientRect().bottom}px`);
			burger.setAttribute('aria-expanded', 'true');
			mobilePanel.hidden = false;
			document.documentElement.classList.add('mobile-nav-open');
			setBackgroundInert(true);
		});
		document.addEventListener('keydown', (e) => {
			// Escape while focus is inside the panel would otherwise dump it on
			// <body>; hand it back to the burger.
			if (e.key === 'Escape') closeMobileNav({ returnFocus: true });
		});
	}

	header.querySelectorAll('[data-mobile-toggle]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const isOpen = btn.getAttribute('aria-expanded') === 'true';
			btn.setAttribute('aria-expanded', String(!isOpen));
			const sublist = btn.nextElementSibling;
			if (sublist) sublist.hidden = isOpen;
		});
	});
}
